
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { updateAgent, fetchElevenLabsAgentDetails } from "@/services/admin/agentService";
import { initiateOutboundCall } from "@/services/ai/elevenLabsService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { AlertCircle, PlayCircle, RefreshCw, Save } from "lucide-react";
import { AgentWithVariables } from "@/types/admin";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface AgentElevenLabsInfoProps {
  agent: AgentWithVariables;
  onUpdate: () => void;
  elevenLabsDetails?: any; // Make this prop optional
}

export const AgentElevenLabsInfo = ({ agent, onUpdate, elevenLabsDetails: initialDetails }: AgentElevenLabsInfoProps) => {
  const [elevenlabsId, setElevenlabsId] = useState(agent.elevenlabs_agent_id || '');
  const [elevenLabsDetails, setElevenLabsDetails] = useState<any>(initialDetails || null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState('');
  const [isTestingAgent, setIsTestingAgent] = useState(false);

  const updateAgentMutation = useMutation({
    mutationFn: (data: { elevenlabs_agent_id: string }) => 
      updateAgent(agent.id, data),
    onSuccess: (result) => {
      if (result.error && result.error.elevenLabsError) {
        toast({
          title: "Partial update",
          description: `Agent ID saved but ElevenLabs verification failed: ${result.error.elevenLabsError}`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Agent updated",
          description: "ElevenLabs Agent ID has been updated successfully.",
        });
      }
      onUpdate();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update agent: ${error instanceof Error ? error.message : "Unknown error"}`,
        variant: "destructive",
      });
    },
  });

  const handleSaveElevenLabsId = () => {
    setFetchError(null);
    if (!elevenlabsId.trim()) {
      toast({
        title: "Missing ID",
        description: "Please enter a valid ElevenLabs Agent ID",
        variant: "destructive",
      });
      return;
    }
    updateAgentMutation.mutate({ elevenlabs_agent_id: elevenlabsId });
  };

  const fetchAgentDetails = async () => {
    if (!agent.elevenlabs_agent_id) {
      toast({
        title: "Missing ElevenLabs Agent ID",
        description: "Please save an ElevenLabs Agent ID first.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setFetchError(null);
    try {
      const result = await fetchElevenLabsAgentDetails(agent.elevenlabs_agent_id);
      if (result.success && result.agent) {
        setElevenLabsDetails(result.agent);
        
        // Extract phone number information
        const phoneNumberId = 
          result.agent.agent_phone_number_id || 
          result.agent.phone_number_id ||
          (result.agent.conversation_config?.agent?.phone_number_id) || 
          null;
        
        // Update the agent with phone number info in the database
        if (phoneNumberId) {
          await updateAgent(agent.id, {
            phone_number_id: phoneNumberId,
          });
        }
        
        toast({
          title: "Agent details fetched",
          description: "Successfully retrieved ElevenLabs agent details.",
        });
      } else {
        const errorMessage = result.error || "Failed to fetch agent details from ElevenLabs.";
        setFetchError(errorMessage);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      setFetchError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestAgent = async () => {
    if (!testPhoneNumber) {
      toast({
        title: "Missing Phone Number",
        description: "Please enter a phone number to test the agent with",
        variant: "destructive",
      });
      return;
    }
    
    if (!agent.elevenlabs_agent_id) {
      toast({
        title: "Missing Agent ID",
        description: "This agent doesn't have an ElevenLabs Agent ID configured",
        variant: "destructive",
      });
      return;
    }

    setIsTestingAgent(true);
    try {
      const result = await initiateOutboundCall(
        testPhoneNumber,
        agent.elevenlabs_agent_id,
        undefined,
        undefined
      );
      
      if (result.success) {
        toast({
          title: "Test Call Initiated",
          description: `Call ID: ${result.callId}. You should receive a call shortly.`,
        });
        setIsTestDialogOpen(false);
      } else {
        toast({
          title: "Test Call Failed",
          description: result.error || "Failed to initiate test call",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Test Call Error",
        description: error instanceof Error ? error.message : "Unknown error occurred",
        variant: "destructive",
      });
    } finally {
      setIsTestingAgent(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-lg font-medium">ElevenLabs Integration</h3>
        
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">ElevenLabs Agent ID</label>
            <Input 
              value={elevenlabsId} 
              onChange={(e) => setElevenlabsId(e.target.value)}
              placeholder="Enter ElevenLabs Agent ID"
            />
          </div>
          <Button 
            onClick={handleSaveElevenLabsId} 
            disabled={updateAgentMutation.isPending || !elevenlabsId.trim()}
          >
            <Save className="h-4 w-4 mr-2" />
            Save ID
          </Button>
        </div>

        {fetchError && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {fetchError}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={fetchAgentDetails} 
            disabled={isLoading || !agent.elevenlabs_agent_id}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Fetch Agent Details
          </Button>
          
          {agent.elevenlabs_agent_id && (
            <Button 
              variant="outline"
              onClick={() => setIsTestDialogOpen(true)}
            >
              <PlayCircle className="h-4 w-4 mr-2" />
              Test Agent
            </Button>
          )}
        </div>
      </div>

      {elevenLabsDetails && (
        <div className="border rounded-md p-4 space-y-4">
          <h4 className="font-medium">ElevenLabs Agent Information</h4>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Name</p>
              <p>{elevenLabsDetails.name || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Voice ID</p>
              <p>{elevenLabsDetails.voice_id || 
                 (elevenLabsDetails.conversation_config?.tts?.voice_id) || 
                 "N/A"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Model ID</p>
              <p>{elevenLabsDetails.model_id || 
                 (elevenLabsDetails.conversation_config?.llm?.model_id) || 
                 "N/A"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Language</p>
              <p>{elevenLabsDetails.language || 
                 (elevenLabsDetails.conversation_config?.agent?.language) || 
                 "N/A"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Phone Number ID</p>
              <p>{elevenLabsDetails.agent_phone_number_id || 
                 elevenLabsDetails.phone_number_id ||
                 (elevenLabsDetails.conversation_config?.agent?.phone_number_id) || 
                 agent.phone_number_id ||
                 "N/A"}</p>
            </div>
            {agent.phone_number && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">Phone Number</p>
                <p>{agent.phone_number || "N/A"}</p>
              </div>
            )}
          </div>

          {(elevenLabsDetails.description || elevenLabsDetails.conversation_config?.description) && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Description</p>
              <p className="text-sm mt-1">{elevenLabsDetails.description || 
                                          elevenLabsDetails.conversation_config?.description}</p>
            </div>
          )}

          {(elevenLabsDetails.prompt || elevenLabsDetails.conversation_config?.prompt) && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">Prompt</p>
              <div className="mt-1 p-3 bg-muted/30 rounded-md text-sm whitespace-pre-wrap">
                {elevenLabsDetails.prompt?.prompt || 
                 elevenLabsDetails.conversation_config?.prompt?.prompt || 
                 "No prompt available"}
              </div>
            </div>
          )}
          
          {(elevenLabsDetails.conversation_config?.agent?.first_message) && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">First Message</p>
              <div className="mt-1 p-3 bg-muted/30 rounded-md text-sm whitespace-pre-wrap">
                {elevenLabsDetails.conversation_config.agent.first_message}
              </div>
            </div>
          )}
          
          {(elevenLabsDetails.conversation_config?.tts) && (
            <div>
              <p className="text-sm font-medium text-muted-foreground">TTS Settings</p>
              <div className="mt-1 p-3 bg-muted/30 rounded-md text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div>Stability: {elevenLabsDetails.conversation_config.tts.stability || "Default"}</div>
                  <div>Similarity Boost: {elevenLabsDetails.conversation_config.tts.similarity_boost || "Default"}</div>
                  <div>Speed: {elevenLabsDetails.conversation_config.tts.speed || "Default"}</div>
                  <div>Optimize Latency: {elevenLabsDetails.conversation_config.tts.optimize_streaming_latency !== undefined ? 
                    elevenLabsDetails.conversation_config.tts.optimize_streaming_latency ? "Yes" : "No" : "Default"}</div>
                </div>
              </div>
            </div>
          )}
          
        </div>
      )}

      {/* Test Agent Dialog */}
      <Dialog open={isTestDialogOpen} onOpenChange={setIsTestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test Agent Call</DialogTitle>
            <DialogDescription>
              Enter a phone number to test this agent with a live call.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="phoneNumber" className="text-sm font-medium">
                Phone Number
              </label>
              <Input
                id="phoneNumber"
                placeholder="+19876543210"
                value={testPhoneNumber}
                onChange={(e) => setTestPhoneNumber(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                Enter a phone number in international format (e.g., +19876543210)
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTestDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleTestAgent} disabled={isTestingAgent || !testPhoneNumber}>
              {isTestingAgent ? "Initiating Call..." : "Make Test Call"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
